import { USAGE_ACTION } from '../constants/sample.constants';

/** 使用履歴の時系列ソート・残数量再計算に必要な最小フィールド */
export type UsageRecordChronologyFields = {
  usageDate: string | Date;
  action?: string;
  quantity?: number;
  remainingQuantity?: number | null;
};

/** 使用日昇順。同時刻は「サンプル新規登録」を先にする */
export function compareUsageRecordsChronologically(
  a: UsageRecordChronologyFields,
  b: UsageRecordChronologyFields
): number {
  const dateA = new Date(a.usageDate).getTime();
  const dateB = new Date(b.usageDate).getTime();
  if (dateA !== dateB) return dateA - dateB;
  if (a.action === USAGE_ACTION.CREATED && b.action !== USAGE_ACTION.CREATED) return -1;
  if (a.action !== USAGE_ACTION.CREATED && b.action === USAGE_ACTION.CREATED) return 1;
  return 0;
}

export function resolveInitialQuantityFromRecords(
  records: UsageRecordChronologyFields[],
  fallbackInitial: number
): number {
  const creation = records.find((r) => r.action === USAGE_ACTION.CREATED);
  return creation?.remainingQuantity ?? fallbackInitial;
}

/** CREATED 以外の quantity 合計 */
export function sumUsedQuantity(records: UsageRecordChronologyFields[]): number {
  return records
    .filter((r) => r.action !== USAGE_ACTION.CREATED)
    .reduce((sum, r) => sum + (r.quantity || 0), 0);
}

/**
 * 時系列で残数量を走査し、行キー → 当該行時点の残数量。
 * 表示順は変えず Map で紐付ける用途向け。
 */
export function computeRemainingByKey<T extends UsageRecordChronologyFields>(
  records: T[],
  initialQuantity: number,
  rowKey: (r: T) => string
): Map<string, number> {
  const sorted = [...records].sort(compareUsageRecordsChronologically);
  let runningRemaining = initialQuantity;
  const remainingByKey = new Map<string, number>();

  for (const record of sorted) {
    if (record.action === USAGE_ACTION.CREATED) {
      runningRemaining = record.remainingQuantity ?? initialQuantity;
    } else {
      runningRemaining = Math.max(0, runningRemaining - (record.quantity || 0));
    }
    remainingByKey.set(rowKey(record), runningRemaining);
  }
  return remainingByKey;
}

/** 配列の表示順を保ったまま remainingQuantity を再計算 */
export function recalculateRemainingQuantitiesPreservingOrder<
  T extends UsageRecordChronologyFields,
>(records: T[], initialQuantity: number, rowKey: (r: T) => string): T[] {
  if (records.length === 0) return records;
  const remainingByKey = computeRemainingByKey(records, initialQuantity, rowKey);
  return records.map((record) => ({
    ...record,
    remainingQuantity: remainingByKey.get(rowKey(record)) ?? record.remainingQuantity ?? 0,
  }));
}

/** 指定行の直前までの残数量（当該行の quantity 適用前） */
export function getRemainingBeforeRecord<T extends UsageRecordChronologyFields>(
  records: T[],
  currentKey: string,
  initialQuantity: number,
  rowKey: (r: T) => string
): number {
  const sorted = [...records].sort(compareUsageRecordsChronologically);
  let runningRemaining = initialQuantity;
  for (const record of sorted) {
    if (rowKey(record) === currentKey) break;
    if (record.action === USAGE_ACTION.CREATED) {
      runningRemaining = record.remainingQuantity ?? initialQuantity;
    } else {
      runningRemaining = Math.max(0, runningRemaining - (record.quantity || 0));
    }
  }
  return runningRemaining;
}

/** 時系列走査で quantity が直前残を超える最初の行。なければ null */
export function findFirstQuantityExceedance(
  records: UsageRecordChronologyFields[],
  initialQuantity: number
): { quantity: number; remaining: number } | null {
  const sorted = [...records].sort(compareUsageRecordsChronologically);
  let runningRemaining = initialQuantity;
  for (const record of sorted) {
    if (record.action === USAGE_ACTION.CREATED) {
      runningRemaining = record.remainingQuantity ?? initialQuantity;
      continue;
    }
    const quantity = record.quantity || 0;
    if (quantity > runningRemaining) {
      return { quantity, remaining: runningRemaining };
    }
    runningRemaining -= quantity;
    if (runningRemaining < 0) {
      return { quantity, remaining: runningRemaining + quantity };
    }
  }
  return null;
}
